import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import HistoryIcon from '@mui/icons-material/History';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Pagination,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { useAuth } from '../auth/useAuth.js';
import { commentsApi, type Comment, type CommentSubject, type CommentTag } from '../api/comments.api.js';
import { APP_LOCALE } from '../utils/locale.js';

/**
 * Comments, wherever somebody needs to say something.
 *
 * The same panel on a client, an application, an arriving payment and a
 * withdrawal — because they are the same act, and three slightly different
 * comment boxes is how an officer's reasoning ends up spread across three
 * places with no single record of it.
 *
 * Editing is allowed and every earlier version is kept. A note that cannot be
 * corrected is one people work around by writing a second note contradicting
 * the first, which leaves an auditor to work out which one counted.
 */

/** The desk an entry was written at, in an officer's words. */
const WRITTEN_ON: Readonly<Record<string, string>> = {
  APPLICATION: 'on the application',
  CUSTOMER: 'on the client',
  PAYMENT: 'on a payment',
  INBOUND_PAYMENT: 'on an incoming payment',
  WITHDRAWAL: 'on an outgoing payment',
  ACCOUNT: 'on an account',
  CARD: 'on a card',
};

/**
 * The filters over a client's whole history, in the order an officer reads a
 * file: the client, how they were accepted, then their money (DEV-95).
 */
const TAG_FILTERS: ReadonlyArray<{ tag: CommentTag; label: string }> = [
  { tag: 'CUSTOMER', label: 'Client' },
  { tag: 'APPLICATION', label: 'Application' },
  { tag: 'INBOUND_PAYMENT', label: 'Incoming payments' },
  { tag: 'WITHDRAWAL', label: 'Outgoing payments' },
  { tag: 'PAYMENT', label: 'Payments' },
  { tag: 'DECISION', label: 'Transaction decisions' },
];

/** Where the transaction a note is about opens, so the note can take you there. */
const TRANSACTION_SOURCE: Readonly<Record<string, string>> = {
  INBOUND_PAYMENT: 'INBOUND',
  WITHDRAWAL: 'WITHDRAWAL',
  PAYMENT: 'PAYMENT',
};

function transactionLink(c: Pick<Comment, 'subjectType' | 'subjectId'>): string | null {
  const source = c.subjectType === null ? undefined : TRANSACTION_SOURCE[c.subjectType];
  if (source === undefined || c.subjectId === null || c.subjectId === '') return null;
  return `/transactions?detailSource=${source}&id=${encodeURIComponent(c.subjectId)}`;
}

export interface CommentsPanelProps {
  subjectType: CommentSubject;
  subjectId: string;
  /** Whose file this lands on, whatever the subject is. */
  customerId: string;
  /** Reading is open to anyone with the page; writing is a role. */
  canWrite?: boolean;
  title?: string;
  /** Fills the height of a side-by-side column rather than growing the page. */
  dense?: boolean;
  /**
   * Read every note about this client, not only the ones written here.
   *
   * Compliance needs one continuous history -- why the client was accepted,
   * then monitoring, then the annual review -- and two panels that each showed
   * half of it produced exactly the confusion DEV-84 describes. Reading widens;
   * writing does not: a note added here is still recorded as written here, so
   * the history says which desk each entry came from.
   */
  wholeClientHistory?: boolean;
}

/**
 * Draft storage. Private browsing, blocked site data and thumbnail capture all
 * make these throw or come back empty, so every access is guarded and the panel
 * works the same when they do — an unsaved draft is a convenience, never the
 * record. The record is the comment, on the server.
 */
function readDraft(key: string): string {
  try {
    return localStorage.getItem(key) ?? '';
  } catch {
    return '';
  }
}

function writeDraft(key: string, value: string): void {
  try {
    if (value === '') localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // Nothing to do: the draft simply will not survive this browser.
  }
}

export function CommentsPanel({
  subjectType,
  subjectId,
  customerId,
  canWrite = true,
  title = 'Comments',
  dense = false,
  wholeClientHistory = false,
}: CommentsPanelProps): JSX.Element {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [removeReason, setRemoveReason] = useState('');

  /**
   * Whether this person may correct this note right now.
   *
   * The author, inside the window; an administrator, always. The server
   * decides for real -- this only stops offering a pencil that would be
   * refused. A note whose window closed while the page was open is still
   * refused on save, with the reason said.
   */
  const mayCorrect = (c: Comment): boolean => {
    if (isAdmin) return true;
    if (user === null || c.authorId !== user.id) return false;
    return c.editableUntil === undefined || Date.now() < new Date(c.editableUntil).getTime();
  };
  // An unsent comment outlives the panel.
  //
  // The draft used to be plain component state, so closing a drawer or taking a
  // decision threw away whatever had been typed, with nothing on screen to say
  // so. Officers reported this as "comments are not saved". It is kept per
  // subject in this browser, restored when the panel comes back, and cleared
  // only once the comment has actually been accepted by the server.
  const draftKey = `norvix.backoffice.comment-draft.${subjectType}.${subjectId}`;
  const [draft, setDraft] = useState(() => readDraft(draftKey));
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [failure, setFailure] = useState<string | null>(null);
  const [historyFor, setHistoryFor] = useState<string | null>(null);
  // Remembered per history, so opening another client starts from all comments.
  const [chosen, setChosen] = useState<{ readId: string; tag: CommentTag | undefined }>({ readId: '', tag: undefined });
  const draftLength = Array.from(draft).length;
  const editLength = Array.from(editDraft).length;

  // What is READ. Writing always uses the panel's own subject below.
  const readType = wholeClientHistory ? 'CUSTOMER' : subjectType;
  const readId = wholeClientHistory ? customerId : subjectId;
  const tag = chosen.readId === readId ? chosen.tag : undefined;
  // Tags narrow a client's whole history; a single payment's thread has none.
  const readTag = readType === 'CUSTOMER' ? tag : undefined;
  const key = ['comments', readType, readId, page, readTag ?? 'ALL'];

  const list = useQuery({
    queryKey: key,
    queryFn: () => commentsApi.list(readType, readId, page, dense ? 5 : 10, readTag),
    enabled: readId !== '',
    // Keeps the filter bar (and the focus in it) on screen while the next tag loads.
    placeholderData: keepPreviousData,
  });

  const history = useQuery({
    queryKey: ['comment-revisions', historyFor ?? ''],
    queryFn: () => commentsApi.revisions(historyFor ?? ''),
    enabled: historyFor !== null,
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['comments', readType, readId] });
  };
  const failed = (err: unknown): void =>
    setFailure(err instanceof Error ? err.message : 'That could not be saved.');

  const updateDraft = (value: string): void => {
    setDraft(value);
    writeDraft(draftKey, value);
  };

  const add = useMutation({
    mutationFn: () => commentsApi.add({ customerId, subjectType, subjectId, body: draft }),
    onSuccess: () => {
      setDraft('');
      writeDraft(draftKey, '');
      setFailure(null);
      setPage(1);
      invalidate();
    },
    onError: failed,
  });

  const edit = useMutation({
    mutationFn: () => commentsApi.edit(editingId ?? '', editDraft),
    onSuccess: () => {
      setEditingId(null);
      setEditDraft('');
      setFailure(null);
      invalidate();
    },
    onError: failed,
  });

  const pin = useMutation({
    mutationFn: (input: { id: string; pinned: boolean }) =>
      commentsApi.setPinned(input.id, input.pinned),
    onSuccess: invalidate,
    onError: failed,
  });

  const remove = useMutation({
    mutationFn: () => commentsApi.remove(removingId ?? '', removeReason.trim()),
    onSuccess: () => {
      setRemovingId(null);
      setRemoveReason('');
      setFailure(null);
      invalidate();
    },
    onError: failed,
  });

  const data = list.data;
  const pages = data === undefined ? 1 : Math.max(1, Math.ceil(data.total / data.pageSize));
  const tagCounts = data?.tagCounts;
  const chooseTag = (next: CommentTag | undefined): void => {
    setChosen({ readId, tag: next });
    setPage(1);
  };

  return (
    <Paper sx={{ p: 2.5, width: '100%', minWidth: 0, boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 1.5, overflowWrap: 'anywhere' }}>
      <Stack direction="row" alignItems="baseline" justifyContent="space-between">
        <Typography variant="h6">{title}</Typography>
        {data !== undefined && (
          <Typography variant="caption" color="text.secondary">
            {data.total === 0
              ? 'None yet'
              : `${String(data.total)} ${data.total === 1 ? 'comment' : 'comments'}`}
          </Typography>
        )}
      </Stack>

      {canWrite && (
        <Box>
          <TextField
            fullWidth
            multiline
            minRows={dense ? 2 : 3}
            size="small"
            placeholder="What should the next person reading this file know?"
            value={draft}
            onChange={(e) => updateDraft(e.target.value)}
            error={draftLength > 10000}
            helperText={`${draftLength.toLocaleString(APP_LOCALE)} / 10,000 characters`}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mt: 1 }}>
            <Button
              size="small"
              variant="contained"
              sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
              disabled={draft.trim().length < 2 || draftLength > 10000 || add.isPending}
              onClick={() => add.mutate()}
            >
              {add.isPending ? 'Saving…' : 'Add comment'}
            </Button>
            <Typography variant="caption" color="text.secondary">
              Signed with your name and kept on the client&rsquo;s file.
            </Typography>
          </Stack>
        </Box>
      )}

      {failure !== null && (
        <Alert severity="warning" onClose={() => setFailure(null)}>
          {failure}
        </Alert>
      )}

      <Divider />

      {/* Filters over the whole history. A tag nobody has written under is
          left out rather than offered as an empty list; the one in use always
          stays, so the way back is never hidden. */}
      {readType === 'CUSTOMER' && tagCounts !== undefined && (
        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" role="group" aria-label="Filter comments">
          <Chip
            size="small"
            label="All comments"
            color={tag === undefined ? 'primary' : 'default'}
            variant={tag === undefined ? 'filled' : 'outlined'}
            onClick={() => chooseTag(undefined)}
            aria-pressed={tag === undefined}
          />
          {TAG_FILTERS.filter((f) => (tagCounts[f.tag] ?? 0) > 0 || f.tag === tag).map((f) => (
            <Chip
              key={f.tag}
              size="small"
              label={`${f.label} · ${String(tagCounts[f.tag] ?? 0)}`}
              color={tag === f.tag ? 'primary' : 'default'}
              variant={tag === f.tag ? 'filled' : 'outlined'}
              onClick={() => chooseTag(tag === f.tag ? undefined : f.tag)}
              aria-pressed={tag === f.tag}
            />
          ))}
        </Stack>
      )}

      {list.isLoading ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Reading the file…
          </Typography>
        </Stack>
      ) : list.isError ? (
        /* A read that failed used to render as "nothing written here yet",
           which tells a reviewer the opposite of the truth: that somebody
           looked at this file and had nothing to say. */
        <Alert severity="warning">
          {list.error instanceof Error
            ? list.error.message
            : 'The comments could not be read. They have not been lost — try again.'}
        </Alert>
      ) : data === undefined || data.comments.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Nothing written here yet.
        </Typography>
      ) : (
        <Stack spacing={1.25}>
          {data.comments.map((c) => (
            <Box
              key={c.id}
              aria-label={c.kind === 'DECISION' ? 'Decision' : 'Comment'}
              sx={{
                p: 1.5,
                borderRadius: 1,
                border: '1px solid',
                borderColor: c.pinned ? 'warning.main' : c.kind === 'DECISION' ? 'primary.main' : 'divider',
                bgcolor: c.pinned ? 'rgba(255,176,32,0.06)' : 'transparent',
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {c.authorName ?? c.authorId} · {new Date(c.createdAt).toLocaleString(APP_LOCALE)}
                    {c.editedAt !== null
                      ? ` · edited by ${c.editorName ?? c.editedBy ?? 'somebody'} ${new Date(c.editedAt).toLocaleString(APP_LOCALE)}`
                      : ''}
                  </Typography>
                  {/* Where it was written. One continuous history is only
                      readable if each entry says which desk it came from --
                      otherwise a note about a payment and a note about the
                      application read as the same kind of thing. */}
                  {c.subjectType !== null && c.subjectType !== subjectType && (() => {
                    const label = WRITTEN_ON[c.subjectType] ?? c.subjectType.toLowerCase();
                    const to = transactionLink(c);
                    // About a transaction: the chip opens it, so the note and
                    // the payment it is about are one click apart (DEV-95).
                    return to === null ? (
                      <Chip size="small" variant="outlined" label={label} sx={{ ml: 0.75, height: 18, fontSize: 11 }} />
                    ) : (
                      <Chip
                        size="small"
                        variant="outlined"
                        color="primary"
                        clickable
                        component={RouterLink}
                        to={to}
                        label={`${label} — open`}
                        sx={{ ml: 0.75, height: 18, fontSize: 11 }}
                      />
                    );
                  })()}

                  {c.kind === 'DECISION' ? (
                    // The decision and its reason, written once when it was
                    // taken; the same entry the client's case file shows.
                    <Box sx={{ mt: 0.5 }}>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Chip size="small" color="primary" variant="outlined" label="Decision" sx={{ height: 18, fontSize: 11 }} />
                        <Typography variant="body2" fontWeight={600}>{c.summary ?? c.body}</Typography>
                      </Stack>
                      {c.summary !== undefined && c.body !== c.summary ? (
                        <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>{c.body}</Typography>
                      ) : null}
                    </Box>
                  ) : editingId === c.id ? (
                    <Box sx={{ mt: 1 }}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        size="small"
                        value={editDraft}
                        onChange={(e) => setEditDraft(e.target.value)}
                        error={editLength > 10000}
                        helperText={`${editLength.toLocaleString(APP_LOCALE)} / 10,000 characters`}
                      />
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Button
                          size="small"
                          variant="contained"
                          disabled={edit.isPending || editDraft.trim().length < 2 || editLength > 10000}
                          onClick={() => edit.mutate()}
                        >
                          Save change
                        </Button>
                        <Button size="small" onClick={() => setEditingId(null)}>
                          Cancel
                        </Button>
                      </Stack>
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                      {c.body}
                    </Typography>
                  )}

                  {c.revisionCount > 0 && (
                    <Button
                      size="small"
                      startIcon={<HistoryIcon fontSize="small" />}
                      sx={{ mt: 0.5, textTransform: 'none' }}
                      onClick={() => setHistoryFor(historyFor === c.id ? null : c.id)}
                    >
                      {historyFor === c.id
                        ? 'Hide earlier versions'
                        : `${String(c.revisionCount)} earlier version${c.revisionCount === 1 ? '' : 's'}`}
                    </Button>
                  )}

                  {historyFor === c.id && (
                    <Box sx={{ mt: 1, pl: 1.5, borderLeft: '2px solid', borderColor: 'divider' }}>
                      {(history.data ?? []).map((r, i) => (
                        <Box key={`${r.replacedAt}-${String(i)}`} sx={{ mb: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            {r.authorId} · replaced {new Date(r.replacedAt).toLocaleString(APP_LOCALE)}
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', opacity: 0.75 }}>
                            {r.body}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>

                <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                  {c.pinned && <Chip size="small" color="warning" variant="outlined" label="Pinned" />}
                  {canWrite && c.kind !== 'DECISION' && (
                    <>
                      <Tooltip title={c.pinned ? 'Unpin' : 'Pin to the top'}>
                        <IconButton
                          size="small"
                          onClick={() => pin.mutate({ id: c.id, pinned: !c.pinned })}
                        >
                          {c.pinned ? (
                            <PushPinIcon fontSize="small" />
                          ) : (
                            <PushPinOutlinedIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                      {mayCorrect(c) && (
                        <Tooltip title="Correct this comment — the earlier version is kept">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setEditingId(c.id);
                              setEditDraft(c.body);
                            }}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {isAdmin && (
                        <Tooltip title="Remove from view — the record and its history are kept">
                          <IconButton
                            size="small"
                            aria-label="Remove comment"
                            onClick={() => {
                              setRemovingId(c.id);
                              setRemoveReason('');
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  )}
                </Stack>
              </Stack>
            </Box>
          ))}
        </Stack>
      )}

      {pages > 1 && (
        <Pagination
          size="small"
          count={pages}
          page={page}
          onChange={(_, next) => setPage(next)}
          sx={{ alignSelf: 'center' }}
        />
      )}

      <Dialog open={removingId !== null} onClose={() => setRemovingId(null)} fullWidth maxWidth="sm">
        <DialogTitle>Remove this comment</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2">
              It disappears from every list. The record, its earlier versions, and who removed it and
              why are kept on the client&rsquo;s file.
            </Typography>
            <TextField
              label="Why is it being removed"
              value={removeReason}
              onChange={(e) => setRemoveReason(e.target.value)}
              multiline
              minRows={2}
              required
              inputProps={{ maxLength: 1000 }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemovingId(null)} disabled={remove.isPending}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={remove.isPending || removeReason.trim().length < 4}
            onClick={() => remove.mutate()}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
